var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useRef, useState } from 'react';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import MainHeading from '../MainHeading';
import useMoveFlow from './useMoveFlow';
import { containerLabel } from './moveHelpers';
import ScanStep from './steps/ScanStep';
import ProductLocationStep from './steps/ProductLocationStep';
import SourceStep from './steps/SourceStep';
import SourcePickStep from './steps/SourcePickStep';
import HoldingPickStep from './steps/HoldingPickStep';
import QtyStep from './steps/QtyStep';
import DestinationStep from './steps/DestinationStep';
import DestinationPickStep from './steps/DestinationPickStep';
import ConfirmStep from './steps/ConfirmStep';
/*
 *
 * Steps:
 *   scan            unified first scan: a location, item, container, or legacy tag,
 *                   classified by get#ScanResult (cart/pack origins rejected)
 *   productLocation a bare product was scanned: scan the location to move it from
 *   source          (after a location scan) the location's contents + scan the unit
 *                   to move (product/asset/container)
 *   sourcePick      cross-type scan ambiguity: pick the interpretation
 *   holdingPick     product in several places: pick loose stock or a container
 *   qty             quantity (and lot when the holding has several)
 *   destination     scan the destination (location or container)
 *   destinationPick destination needs a selection: pick a sub-container/pallet
 *                   or move direct to the location / onto the root pallet
 *   confirm         summary + confirm
 *
 * This component owns the flow state and the service calls; each step is a
 * presentational component under ./steps that receives the slice of state and
 * the callbacks it needs.
 */
var MoveV2 = function (_a) {
    var setIsLoading = _a.setIsLoading;
    var t = useTranslation().t;
    var _b = useMoveFlow(), validateOrigin = _b.validateOrigin, fetchContents = _b.fetchContents, resolveScanResult = _b.resolveScanResult, resolveSource = _b.resolveSource, resolveDestination = _b.resolveDestination, move = _b.move;
    var _c = useState('scan'), step = _c[0], setStep = _c[1];
    var _d = useState(''), originLocation = _d[0], setOriginLocation = _d[1];
    var _e = useState(null), contents = _e[0], setContents = _e[1];
    var _f = useState(''), scanError = _f[0], setScanError = _f[1];
    var _g = useState(0), focusSignal = _g[0], setFocusSignal = _g[1];
    var _h = useState([]), sourceMatches = _h[0], setSourceMatches = _h[1];
    var _j = useState(null), productMatch = _j[0], setProductMatch = _j[1];
    // unified first scan: the product awaiting a location scan, and the origin derived from a unit scan
    var _k = useState(''), pendingProductId = _k[0], setPendingProductId = _k[1];
    var _l = useState(''), pendingResolvedOrigin = _l[0], setPendingResolvedOrigin = _l[1];
    var _m = useState(null), moveUnit = _m[0], setMoveUnit = _m[1];
    var _o = useState(null), selectedLotId = _o[0], setSelectedLotId = _o[1];
    var _p = useState(''), quantityInput = _p[0], setQuantityInput = _p[1];
    var _q = useState(''), qtyError = _q[0], setQtyError = _q[1];
    var _r = useState([]), destMatches = _r[0], setDestMatches = _r[1];
    var _s = useState(null), pendingDestMatch = _s[0], setPendingDestMatch = _s[1];
    var _t = useState(null), destSelection = _t[0], setDestSelection = _t[1];
    var _u = useState(false), moving = _u[0], setMoving = _u[1];
    var bumpFocus = function () { return setFocusSignal(function (n) { return n + 1; }); };
    // Step navigation history so Back returns to the actual previous step. The first scan branches,
    // so "previous" is path-dependent; a stack handles every path. Kept in refs (it doesn't drive
    // rendering). goTo pushes, goBack pops, and Start Over (resetAll) clears it.
    var stepRef = useRef(step);
    stepRef.current = step;
    var historyRef = useRef([]);
    var resetAll = useCallback(function () {
        historyRef.current = [];
        setStep('scan');
        setOriginLocation('');
        setContents(null);
        setScanError('');
        setSourceMatches([]);
        setProductMatch(null);
        setPendingProductId('');
        setPendingResolvedOrigin('');
        setMoveUnit(null);
        setSelectedLotId(null);
        setQuantityInput('');
        setQtyError('');
        setDestMatches([]);
        setPendingDestMatch(null);
        setDestSelection(null);
        bumpFocus();
    }, []);
    // advance one step, remembering where we came from
    var goTo = useCallback(function (next) {
        historyRef.current = __spreadArray(__spreadArray([], historyRef.current, true), [stepRef.current], false);
        setScanError('');
        setStep(next);
        bumpFocus();
    }, []);
    // return to the previous step (Start Over / resetAll clears everything instead)
    var goBack = useCallback(function () {
        var stack = historyRef.current;
        if (stack.length === 0) {
            resetAll();
            return;
        }
        historyRef.current = stack.slice(0, -1);
        setScanError('');
        setQtyError('');
        setStep(stack[stack.length - 1]);
        bumpFocus();
    }, [resetAll]);
    // source
    var applyHolding = useCallback(function (match, holding) {
        var _a;
        var lots = holding.lots || [];
        setMoveUnit({
            kind: 'product',
            label: (match.productSku || match.productId) + " " + (match.productName ? '· ' + match.productName : ''),
            productId: match.productId,
            holding: holding,
            lots: lots,
            maxQuantity: holding.quantityOnHand
        });
        setSelectedLotId(lots.length === 1 ? (_a = lots[0].lotId) !== null && _a !== void 0 ? _a : null : null);
        setQuantityInput('');
        setQtyError('');
        goTo('qty');
    }, [goTo]);
    var applySourceMatch = useCallback(function (match) {
        setScanError('');
        if (match.matchType === 'container' && match.container) {
            setMoveUnit({
                kind: match.moveUnitKind || (match.container.isPallet ? 'pallet' : 'carton'),
                label: containerLabel(match.container),
                containerId: match.container.containerId,
                isEmpty: match.container.isEmpty
            });
            goTo('destination');
            return;
        }
        if (match.matchType === 'asset') {
            setMoveUnit({
                kind: 'asset',
                label: t('Asset') + " " + match.assetId + " \u00B7 " + (match.productSku || match.productName || match.productId),
                assetId: match.assetId,
                lots: match.lots || [],
                maxQuantity: match.quantityOnHand
            });
            setSelectedLotId(null);
            setQuantityInput('');
            setQtyError('');
            goTo('qty');
            return;
        }
        // product
        var holdings = match.holdings || [];
        setProductMatch(match);
        if (holdings.length === 1) {
            applyHolding(match, holdings[0]);
        }
        else {
            goTo('holdingPick');
        }
    }, [applyHolding, t, goTo]);
    var handleSourceScan = useCallback(function (scanId) { return __awaiter(void 0, void 0, void 0, function () {
        var res, matches;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setScanError('');
                    setIsLoading(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, , 3, 4]);
                    return [4 /*yield*/, resolveSource(originLocation, scanId)];
                case 2:
                    res = _a.sent();
                    matches = res.matches || [];
                    if (!res.ok || matches.length === 0) {
                        setScanError(res.errorMessage || t('Nothing found for that scan'));
                        bumpFocus();
                        return [2 /*return*/];
                    }
                    if (matches.length > 1) {
                        setSourceMatches(matches);
                        setPendingResolvedOrigin('');
                        goTo('sourcePick');
                        return [2 /*return*/];
                    }
                    applySourceMatch(matches[0]);
                    return [3 /*break*/, 4];
                case 3:
                    setIsLoading(false);
                    return [7 /*endfinally*/];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [originLocation, resolveSource, applySourceMatch, setIsLoading, t, goTo]);
    // classify the scan and route to the right step
    var applyScanMatch = useCallback(function (match, resolvedOrigin) { return __awaiter(void 0, void 0, void 0, function () {
        var c;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setScanError('');
                    if (!(match.matchType === 'location' && match.locationSeqId)) return [3 /*break*/, 2];
                    setOriginLocation(match.locationSeqId);
                    return [4 /*yield*/, fetchContents(match.locationSeqId)];
                case 1:
                    c = _a.sent();
                    setContents(c.ok ? c : null);
                    goTo('source');
                    return [2 /*return*/];
                case 2:
                    // a product scanned with no location: ask for the location next
                    if (match.matchType === 'product' && match.requiresLocation) {
                        setPendingProductId(match.productId || '');
                        goTo('productLocation');
                        return [2 /*return*/];
                    }
                    // a unit (container/asset) or a product already resolved with holdings: origin is known/derived
                    if (resolvedOrigin)
                        setOriginLocation(resolvedOrigin);
                    applySourceMatch(match);
                    return [2 /*return*/];
            }
        });
    }); }, [fetchContents, applySourceMatch, goTo]);
    var handleScan = useCallback(function (scanId) { return __awaiter(void 0, void 0, void 0, function () {
        var res, matches;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setScanError('');
                    setIsLoading(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, , 4, 5]);
                    return [4 /*yield*/, resolveScanResult(scanId)];
                case 2:
                    res = _a.sent();
                    matches = res.matches || [];
                    if (!res.ok || matches.length === 0) {
                        setScanError(res.errorMessage || t('Nothing found for that scan'));
                        bumpFocus();
                        return [2 /*return*/];
                    }
                    if (matches.length > 1) {
                        setSourceMatches(matches);
                        setPendingResolvedOrigin(res.resolvedOriginLocationSeqId || '');
                        goTo('sourcePick');
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, applyScanMatch(matches[0], res.resolvedOriginLocationSeqId)];
                case 3:
                    _a.sent();
                    return [3 /*break*/, 5];
                case 4:
                    setIsLoading(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [resolveScanResult, applyScanMatch, setIsLoading, t, goTo]);
    var handleProductLocationScan = useCallback(function (scanId) { return __awaiter(void 0, void 0, void 0, function () {
        var loc, res, matches;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setScanError('');
                    setIsLoading(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, , 4, 5]);
                    return [4 /*yield*/, validateOrigin(scanId)];
                case 2:
                    loc = _a.sent();
                    if (!loc.ok || !loc.locationSeqId) {
                        setScanError(loc.error || t('Incorrect location, scan again'));
                        bumpFocus();
                        return [2 /*return*/];
                    }
                    setOriginLocation(loc.locationSeqId);
                    return [4 /*yield*/, resolveSource(loc.locationSeqId, pendingProductId, pendingProductId)];
                case 3:
                    res = _a.sent();
                    matches = res.matches || [];
                    if (!res.ok || matches.length === 0) {
                        setScanError(res.errorMessage || t('That product is not at this location'));
                        bumpFocus();
                        return [2 /*return*/];
                    }
                    if (matches.length > 1) {
                        setSourceMatches(matches);
                        setPendingResolvedOrigin('');
                        goTo('sourcePick');
                        return [2 /*return*/];
                    }
                    applySourceMatch(matches[0]);
                    return [3 /*break*/, 5];
                case 4:
                    setIsLoading(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [validateOrigin, resolveSource, pendingProductId, applySourceMatch, setIsLoading, t, goTo]);
    // quantity / lot
    var submitQuantity = useCallback(function () {
        if (!moveUnit)
            return;
        var qty = Number(quantityInput);
        if (!/^[1-9]\d*$/.test(quantityInput.trim()) || !qty) {
            setQtyError(t('Enter a valid quantity'));
            return;
        }
        var lots = moveUnit.lots || [];
        var selectedLot = selectedLotId != null ? lots.find(function (l) { var _a; return ((_a = l.lotId) !== null && _a !== void 0 ? _a : null) === selectedLotId; }) : null;
        if (lots.length > 1 && selectedLot == null) {
            setQtyError(t('Select a lot'));
            return;
        }
        var maxQty = selectedLot ? selectedLot.quantity : moveUnit.maxQuantity || 0;
        if (qty > maxQty) {
            setQtyError(t('Only') + " " + maxQty + " " + t('on hand'));
            return;
        }
        setQtyError('');
        goTo('destination');
    }, [moveUnit, quantityInput, selectedLotId, t, goTo]);
    // destination
    var applyDestMatch = useCallback(function (match) {
        if (!moveUnit)
            return;
        if (match.matchType === 'location') {
            var tree = match.containersAtLocation || [];
            if (tree.length > 0) {
                // offer pallet/sub-container selection, or direct to location
                setPendingDestMatch(match);
                goTo('destinationPick');
                return;
            }
            setDestSelection({
                destType: 'location',
                toLocationSeqId: match.locationSeqId,
                label: t('Location') + " " + match.locationSeqId
            });
            goTo('confirm');
            return;
        }
        // container destination
        var container = match.container;
        if (match.allowIntoRoot && (match.childCartons || []).length > 0) {
            setPendingDestMatch(match);
            goTo('destinationPick');
            return;
        }
        setDestSelection({
            destType: 'container',
            toContainerId: container.containerId,
            label: containerLabel(container)
        });
        goTo('confirm');
    }, [moveUnit, t, goTo]);
    var handleDestinationScan = useCallback(function (scanId) { return __awaiter(void 0, void 0, void 0, function () {
        var res, matches;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!moveUnit)
                        return [2 /*return*/];
                    setScanError('');
                    setIsLoading(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, , 3, 4]);
                    return [4 /*yield*/, resolveDestination(scanId, moveUnit.kind, moveUnit.containerId, originLocation)];
                case 2:
                    res = _a.sent();
                    matches = res.matches || [];
                    if (!res.ok || matches.length === 0) {
                        setScanError(res.errorMessage || t('No location or container found'));
                        bumpFocus();
                        return [2 /*return*/];
                    }
                    if (matches.length > 1) {
                        setDestMatches(matches);
                        setPendingDestMatch(null);
                        goTo('destinationPick');
                        return [2 /*return*/];
                    }
                    applyDestMatch(matches[0]);
                    return [3 /*break*/, 4];
                case 3:
                    setIsLoading(false);
                    return [7 /*endfinally*/];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [moveUnit, originLocation, resolveDestination, applyDestMatch, setIsLoading, t, goTo]);
    // ---------- confirm ----------
    var handleConfirm = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var res;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!moveUnit || !destSelection)
                        return [2 /*return*/];
                    setMoving(true);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, , 3, 4]);
                    return [4 /*yield*/, move({
                            sourceType: moveUnit.kind === 'product' ? 'product' : moveUnit.kind === 'asset' ? 'asset' : 'container',
                            productId: moveUnit.productId,
                            assetId: moveUnit.assetId,
                            containerId: moveUnit.containerId,
                            fromLocationSeqId: originLocation,
                            fromContainerId: ((_a = moveUnit.holding) === null || _a === void 0 ? void 0 : _a.containerId) || undefined,
                            lotId: selectedLotId || undefined,
                            quantity: moveUnit.kind === 'product' || moveUnit.kind === 'asset' ? Number(quantityInput) : undefined,
                            destType: destSelection.destType,
                            toLocationSeqId: destSelection.toLocationSeqId,
                            toContainerId: destSelection.toContainerId
                        })];
                case 2:
                    res = _b.sent();
                    if (res.ok) {
                        notification.success({
                            message: t('Moved'),
                            description: res.message || t('Move complete.')
                        });
                        resetAll();
                    }
                    else {
                        notification.error({
                            message: t('Error'),
                            description: res.error || t('Move failed')
                        });
                    }
                    return [3 /*break*/, 4];
                case 3:
                    setMoving(false);
                    return [7 /*endfinally*/];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [moveUnit, destSelection, originLocation, selectedLotId, quantityInput, move, resetAll, t]);
    return (_jsxs("div", __assign({ className: 'px-4 sm:px-8 lg:px-16 pt-1 lg:pt-3 pb-16 h-screen w-full sm:w-4/5 md:w-3/5 lg:w-2/5 mx-auto' }, { children: [_jsx(MainHeading, { heading: t('Move') }, void 0), _jsxs("div", __assign({ className: 'bg-white p-5 rounded-lg shadow-sm' }, { children: [step === 'scan' && (_jsx(ScanStep, { scanError: scanError, focusSignal: focusSignal, onScan: handleScan }, void 0)), step === 'productLocation' && (_jsx(ProductLocationStep, { scanError: scanError, focusSignal: focusSignal, onScan: handleProductLocationScan, onBack: goBack }, void 0)), step === 'source' && (_jsx(SourceStep, { originLocation: originLocation, scanError: scanError, focusSignal: focusSignal, contents: contents, onScan: handleSourceScan, onBack: goBack }, void 0)), step === 'sourcePick' && (_jsx(SourcePickStep, { sourceMatches: sourceMatches, onPick: function (m) {
                            void applyScanMatch(m, pendingResolvedOrigin || undefined);
                        }, onBack: goBack }, void 0)), step === 'holdingPick' && (_jsx(HoldingPickStep, { productMatch: productMatch, onPick: function (h) { return productMatch && applyHolding(productMatch, h); }, onBack: goBack }, void 0)), step === 'qty' && (_jsx(QtyStep, { moveUnit: moveUnit, selectedLotId: selectedLotId, quantityInput: quantityInput, qtyError: qtyError, onSelectLot: setSelectedLotId, onQuantityChange: setQuantityInput, onSubmit: submitQuantity, onBack: goBack }, void 0)), step === 'destination' && (_jsx(DestinationStep, { moveUnit: moveUnit, originLocation: originLocation, quantityInput: quantityInput, scanError: scanError, focusSignal: focusSignal, onScan: handleDestinationScan, onBack: goBack }, void 0)), step === 'destinationPick' && (_jsx(DestinationPickStep, { pendingDestMatch: pendingDestMatch, destMatches: destMatches, moveUnit: moveUnit, onApplyDest: applyDestMatch, onSelect: function (sel) {
                            setDestSelection(sel);
                            goTo('confirm');
                        }, onBack: goBack }, void 0)), step === 'confirm' && (_jsx(ConfirmStep, { moveUnit: moveUnit, originLocation: originLocation, quantityInput: quantityInput, selectedLotId: selectedLotId, destSelection: destSelection, moving: moving, onConfirm: handleConfirm, onBack: goBack }, void 0))] }), void 0), step !== 'scan' && (_jsx("div", __assign({ className: 'mt-3 text-center' }, { children: _jsx("button", __assign({ type: 'button', onClick: resetAll, className: 'text-xs font-medium text-gray-500 underline' }, { children: t('Start Over') }), void 0) }), void 0))] }), void 0));
};
export default MoveV2;
